import classNames from 'classnames';
import Table from 'rc-table';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
    DEPTH,
    CREATE_ORDER_SIDE,
    SPOT_ORDER_STATUS_LANG_MAP,
    ORDER_TYPE,
    ACCOUNT_TYPES,
} from 'src/common/constants';
import { ORDER_STATUS } from 'src/common/constants';
import { currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { Down, Up } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
    getConditionalOrderDetail,
    getLimitOrMarketOrderDetail,
} from 'src/pages/spot/_redux/spotPageActions';
import { OrderTableContentProps, Symbol, TradeOrder } from 'src/types';
import {
    ConditionalOrderDetail,
    ConditionalOrderDetails,
    LimitOrderDetail,
    LimitOrderDetails,
} from 'src/types/orders';

const OrderHistoryTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [filterOrderHistory, setFilterOrderHistory] = useState([] as Array<TradeOrder>);
    const currentSymbol: Symbol = useAppSelector((state) => state.spotPage.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const tradeOrders: Array<TradeOrder> = useAppSelector(
        (state) => state.spotPage.tradeOrderHistory,
    );

    const symbols = useAppSelector((state) => state.globalData.configV2.symbol);
    const [limitOrderDetails, setLimitOrderDetails] = useState({} as LimitOrderDetails);
    const [conditionalOrderDetails, setConditionalOrderDetails] = useState(
        {} as ConditionalOrderDetails,
    );
    const disptach = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? tradeOrders
            : tradeOrders.filter((tradeOrder) => tradeOrder.symbolId === symbolId);
        filtered = [...filtered].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
        setFilterOrderHistory(filtered);
    }, [tradeOrders, symbolId, isShowAll]);

    const columns = [
        {
            title: t('spot_pairs'),
            key: 'spotPairs',
            width: '5%',
            render: (order: TradeOrder) => {
                return (
                    <span>
                        {order.baseTokenId}
                        <span className="greyText">/{order.quoteTokenName}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            width: '5%',
            render: (order: TradeOrder) => {
                return (
                    <span>
                        {order.triggerPrice && <>{t('conditional')} </>}
                        {t(order.type.toLowerCase())}
                    </span>
                );
            },
        },
        {
            title: t('direction'),
            key: 'direction',
            width: '4%',
            render: (order: TradeOrder) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText: order.side === CREATE_ORDER_SIDE.BUY,
                            },
                            {
                                redText: order.side === CREATE_ORDER_SIDE.SELL,
                            },
                        )}
                    >
                        {t(order.side.toLowerCase())}
                    </span>
                );
            },
        },
        {
            title: t('order_value'),
            key: 'orderValue',
            width: '5%',
            render: (order: TradeOrder) => {
                const orderValue =
                    order.type === ORDER_TYPE.MARKET && order.side === CREATE_ORDER_SIDE.BUY
                        ? order.origQty
                        : order.price * order.origQty;
                return (
                    <span>
                        {currencyFormatter(
                            orderValue,
                            DEPTH[symbols[order.symbolId].minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            width: '6%',
            render: (order: TradeOrder) => {
                if (order.type === ORDER_TYPE.MARKET) {
                    return <span>{t('market')}</span>;
                }
                return (
                    <span>
                        {currencyFormatter(
                            order.price,
                            DEPTH[symbols[order.symbolId].minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_qty'),
            key: 'orderQty',
            width: '5%',
            render: (order: TradeOrder) => {
                if (order.type === ORDER_TYPE.MARKET && order.side === CREATE_ORDER_SIDE.BUY) {
                    return <span>--</span>;
                }
                return (
                    <span>
                        {currencyFormatter(
                            order.origQty,
                            DEPTH[symbols[order.symbolId].basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('avg_filled_price'),
            key: 'avgFilledPrice',
            width: '7%',
            render: (order: TradeOrder) => {
                return (
                    <span>
                        {order.avgPrice ? (
                            <>
                                {currencyFormatter(
                                    order.avgPrice,
                                    DEPTH[symbols[order.symbolId].minPricePrecision],
                                )}{' '}
                                <span className="greyText">{order.quoteTokenId}</span>
                            </>
                        ) : (
                            '--'
                        )}
                    </span>
                );
            },
        },
        {
            title: t('filled_qty'),
            key: 'filledQty',
            width: '6%',
            render: (order: TradeOrder) => {
                return (
                    <span>
                        {order.executedQty ? (
                            <>
                                {currencyFormatter(
                                    order.executedQty,
                                    DEPTH[symbols[order.symbolId].basePrecision],
                                )}{' '}
                                <span className="greyText">{order.baseTokenId}</span>
                            </>
                        ) : (
                            '--'
                        )}
                    </span>
                );
            },
        },
        {
            title: t('trigger_price'),
            key: 'triggerPrice',
            width: '7%',
            render: (order: TradeOrder) => {
                return (
                    <span>
                        {order.triggerPrice ? (
                            <>
                                {Number(order.quotePrice) > Number(order.triggerPrice) ? '≤' : '≥'}{' '}
                                {currencyFormatter(
                                    order.triggerPrice,
                                    DEPTH[symbols[order.symbolId].minPricePrecision],
                                )}{' '}
                                <span className="greyText">{order.quoteTokenId}</span>
                            </>
                        ) : (
                            '--'
                        )}
                    </span>
                );
            },
        },
        {
            title: t('trigger_time'),
            key: 'triggerTime',
            width: '6%',
            render: (order: TradeOrder) => {
                return (
                    <span>
                        {order.triggerPrice && order.triggerTime && Number(order.triggerTime) > 0
                            ? DateTimeFormatter(Number(order.triggerTime))
                            : '--'}
                    </span>
                );
            },
        },
        {
            title: t('order_status'),
            key: 'orderStatus',
            width: '6%',
            render: (order: TradeOrder) => {
                if (
                    order.status === ORDER_STATUS.CANCELED &&
                    order.executedQty &&
                    order.executedQty > 0
                ) {
                    return (
                        <span>{t(SPOT_ORDER_STATUS_LANG_MAP[ORDER_STATUS.PARTIALLY_FILLED])}</span>
                    );
                }

                return <span>{t(SPOT_ORDER_STATUS_LANG_MAP[order.status])}</span>;
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            width: '9%',
            render: (order: TradeOrder) => {
                return <span>{DateTimeFormatter(Number(order.time))}</span>;
            },
        },
        Table.EXPAND_COLUMN,
    ];

    const limitOrderDetailcolumns = [
        {
            title: t('filled_value'),
            key: 'filledValue',
            width: '3%',
            render: (order: LimitOrderDetail) => {
                const orderValue = order.price * order.quantity;
                return (
                    <span>
                        {currencyFormatter(
                            orderValue,
                            DEPTH[symbols[order.symbolId].quotePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_price'),
            key: 'filledPrice',
            width: '5%',
            render: (order: LimitOrderDetail) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.price,
                            DEPTH[symbols[order.symbolId].minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_qty'),
            key: 'filledQty',
            width: '3%',
            render: (order: LimitOrderDetail) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.quantity,
                            DEPTH[symbols[order.symbolId].basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('trading_fee'),
            key: 'tradingFee',
            width: '5%',
            render: (order: LimitOrderDetail) => {
                return (
                    <span>
                        {order.fee} <span className="greyText">{order.feeTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_time'),
            key: 'filledTime',
            width: '5%',
            render: (order: LimitOrderDetail) => {
                return <span>{DateTimeFormatter(Number(order.time))}</span>;
            },
        },
    ];

    const conditionalOrderDetailscolumns = [
        {
            title: t('order_value'),
            key: 'orderValue',
            width: '3%',
            render: (conditionalOrder: ConditionalOrderDetail) => {
                const { planOrder: order } = conditionalOrder;
                const orderValue =
                    order.type === ORDER_TYPE.MARKET && order.side === CREATE_ORDER_SIDE.BUY
                        ? order.origQty
                        : order.price * order.origQty;
                return (
                    <span>
                        {currencyFormatter(
                            orderValue,
                            DEPTH[symbols[order.symbolId].quotePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            width: '5%',
            render: (conditionalOrder: ConditionalOrderDetail) => {
                const { planOrder: order } = conditionalOrder;
                if (order.type === ORDER_TYPE.MARKET) {
                    return <span>{t('market')}</span>;
                }
                return (
                    <span>
                        {currencyFormatter(
                            order.price,
                            DEPTH[symbols[order.symbolId].minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_qty'),
            key: 'filledQty',
            width: '3%',
            render: (conditionalOrder: ConditionalOrderDetail) => {
                const { planOrder: order } = conditionalOrder;
                return (
                    <span>
                        {order.executedQty ? (
                            <>
                                {currencyFormatter(
                                    order.executedQty,
                                    DEPTH[symbols[order.symbolId].basePrecision],
                                )}{' '}
                                <span className="greyText">{order.baseTokenId}</span>
                            </>
                        ) : (
                            '--'
                        )}
                    </span>
                );
            },
        },
        {
            title: t('unfilled_qty'),
            key: 'unfilledQty',
            width: '5%',
            render: (conditionalOrder: ConditionalOrderDetail) => {
                const { planOrder: order } = conditionalOrder;
                return (
                    <span>
                        {order.executedQty ? (
                            <>
                                {currencyFormatter(
                                    order.origQty - order.executedQty,
                                    DEPTH[symbols[order.symbolId].basePrecision],
                                )}{' '}
                                <span className="greyText">{order.baseTokenId}</span>
                            </>
                        ) : (
                            '--'
                        )}
                    </span>
                );
            },
        },
        {
            title: t('filled_time'),
            key: 'filledTime',
            width: '5%',
            render: (conditionalOrder: ConditionalOrderDetail) => {
                const { planOrder: order } = conditionalOrder;
                return <span>{DateTimeFormatter(Number(order.time))}</span>;
            },
        },
        {
            title: t('order_id'),
            key: 'orderId',
            width: '6%',
            render: (conditionalOrder: ConditionalOrderDetail) => {
                const { planOrder: order } = conditionalOrder;
                return <span>{order.orderId}</span>;
            },
        },
    ];

    const customExpandIcon = (props: any) => {
        const record: TradeOrder = props.record;
        return (
            (record.status === ORDER_STATUS.FILLED ||
                record.status === ORDER_STATUS.ORDER_FILLED ||
                record.status === ORDER_STATUS.PARTIALLY_FILLED ||
                (record.status === ORDER_STATUS.CANCELED &&
                    record.executedQty &&
                    record.executedQty > 0)) && (
                <FlexContainer
                    styles={{ alignItems: 'center', color: colors.primary, cursor: 'pointer' }}
                    onClick={(e) => {
                        props.onExpand(record, e);
                    }}
                >
                    {t('details')}
                    {props.expanded ? (
                        <Up stroke={colors.primary} style={{ marginLeft: '5px' }} />
                    ) : (
                        <Down stroke={colors.primary} style={{ marginLeft: '5px' }} />
                    )}
                </FlexContainer>
            )
        );
    };

    const customOnExpand = async (expanded: boolean, record: TradeOrder) => {
        if (!expanded) {
            return;
        }
        if (record.triggerPrice) {
            const conditionalOrderDetail: ConditionalOrderDetail = await disptach(
                getConditionalOrderDetail({
                    order_id: record.orderId,
                    account_type: ACCOUNT_TYPES.CURRENCY_ACCOUNT,
                }),
            );

            setConditionalOrderDetails({
                ...conditionalOrderDetails,
                [`${record.orderId}`]: [conditionalOrderDetail],
            });
        } else {
            const limitOrderDetail: LimitOrderDetail[] = await disptach(
                getLimitOrMarketOrderDetail({
                    order_id: record.orderId,
                    limit: '20',
                }),
            );
            setLimitOrderDetails({
                ...limitOrderDetails,
                [record.orderId]: limitOrderDetail,
            });
        }
    };
    return (
        <StyledTable
            style={{ minWidth: '1529px' }}
            columns={columns}
            data={filterOrderHistory}
            rowKey="orderId"
            expandable={{
                expandedRowRender: (record: TradeOrder, index: number, expanded: boolean) =>
                    expanded &&
                    (conditionalOrderDetails[record.orderId] ||
                        limitOrderDetails[record.orderId]) ? (
                        <FlexContainer
                            styles={{
                                backgroundColor: colors.background.secondary.v3,
                                marginRight: '16px',
                            }}
                        >
                            <StyledTable
                                className={'detail-table'}
                                columns={
                                    record.triggerPrice
                                        ? conditionalOrderDetailscolumns
                                        : limitOrderDetailcolumns
                                }
                                data={
                                    record.triggerPrice
                                        ? conditionalOrderDetails[record.orderId]
                                        : limitOrderDetails[record.orderId]
                                }
                                rowKey={record.triggerPrice ? 'orderId' : 'tradeId'}
                            />
                        </FlexContainer>
                    ) : null,
                columnTitle: t('action'),
                columnWidth: '4%',
                expandIcon: customExpandIcon,
                onExpand: customOnExpand,
            }}
        />
    );
};

export { OrderHistoryTable };
